import type {PermissionType} from '@hconnect/apiclient'
import {FlexPage} from '@hconnect/common/components'
import {FilterContrastProvider} from '@hconnect/common/styles'
import {DateRangePicker} from '@hconnect/uikit/src/lib2/components/simpleDatePicker'
import {defaultQuickSelectionItems} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/dateRangePickerHelpers'
import {useUpmPlantTimezone, useUpmPlant} from '@hconnect/upm'
import {Grid, Stack, useMediaQuery, useTheme} from '@mui/material'
import {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  JtbdExportButton,
  JtbdFrequencyDropdownSelector,
  JtbdRolesDropdownSelector
} from '../components'
import {FEATURE_NAMESPACE, JTBD_PERMISSIONS} from '../consts'
import {JtbdDashboardContainer} from '../containers'
import {HrocJtbdRole} from '../enums'
import {
  getPermissionTypeByRole,
  useJobsToBeDoneDefaultRole,
  useJobsToBeDonePageState
} from '../hooks'
import {useHasAnyOfEditPermissions} from '../utils'

type Props = {
  plantId: string
  titleSlot?: React.ReactNode
  inverted?: boolean
}

export const JobsToBeDonePage = ({plantId, titleSlot, inverted = false}: Props) => {
  const upmPlant = useUpmPlant({plantId})

  const {t} = useTranslation(FEATURE_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)

  const hasAnyOfTheEditJtbdPermissions = useHasAnyOfEditPermissions(JTBD_PERMISSIONS)
  const defaultRole = useJobsToBeDoneDefaultRole(hasAnyOfTheEditJtbdPermissions[0])

  const {
    hrocRoleType: selectedRole = defaultRole,
    setHrocRoleType: onSelectedRoleChanged,
    selectedFrequency,
    setSelectedFrequency,
    dateRange,
    setDateRange
  } = useJobsToBeDonePageState()

  const [availableFrequencies, setAvailableFrequencies] = useState<string[]>([])

  const handleFrequenciesLoaded = useCallback((frequencies: string[]) => {
    setAvailableFrequencies(frequencies)
  }, [])

  const isEditAllowed = useMemo(() => {
    const permission = getPermissionTypeByRole(selectedRole as PermissionType)
    if (!permission) return false
    return JTBD_PERMISSIONS.includes(permission)
  }, [selectedRole])

  return (
    <FlexPage
      title={titleSlot}
      nameBodySx={{pb: 0}}
      nameHeaderSx={{mt: 0}}
      headerActionContent={
        <FilterContrastProvider isContrastMode={inverted}>
          <Stack direction="row" spacing={2}>
            <JtbdExportButton
              plantUpmId={upmPlant.upmId}
              plantCode={upmPlant.plantId}
              plantName={upmPlant.name}
              associatedRole={selectedRole}
            />

            <DateRangePicker
              timeRangeParam={dateRange}
              onTimeRangeParamChange={setDateRange}
              singleDateSwitchLabel={t('quickSelection.pickSingleDate')}
              selectionItems={defaultQuickSelectionItems(t, timezone)}
              timezone={timezone}
              withForm={true}
              isContrastMode={inverted}
            />
            <JtbdRolesDropdownSelector
              selectedRole={selectedRole as HrocJtbdRole}
              onSelectedRoleChanged={onSelectedRoleChanged}
            />
            <JtbdFrequencyDropdownSelector
              selectedFrequency={selectedFrequency}
              onSelectedFrequencyChanged={setSelectedFrequency}
              frequencies={availableFrequencies}
            />
          </Stack>
        </FilterContrastProvider>
      }
    >
      <Layout
        table={
          <JtbdDashboardContainer
            associatedRole={selectedRole}
            timezone={timezone}
            plantUpmId={upmPlant.upmId}
            isEditAllowed={isEditAllowed}
            selectedFrequency={selectedFrequency}
            dateRange={dateRange}
            onFrequenciesLoaded={handleFrequenciesLoaded}
          />
        }
      />
    </FlexPage>
  )
}

type LayoutProps = {
  table?: React.ReactNode
  form?: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({table, form}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const spacing = isMobile ? 1 : 2

  return (
    <Grid container spacing={spacing} flex={1}>
      <Grid item xs={12} sm={12} lg={12}>
        {table}
      </Grid>
      <Grid item container xs={12} sm={12}>
        {form}
      </Grid>
    </Grid>
  )
}
